import React, { Component } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  CustomInput,
} from "reactstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import axios from "axios";
import Select from "react-select";
import { END_POINT } from "../../../constants/defaultValues";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";

/**
 * Webhook Management Settings
 * Quản lý Webhook subscriptions
 */
class WebhookManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      webhooks: [],
      logs: [],

      // Modal states
      showCreateModal: false,
      showSecretModal: false,
      showLogsModal: false,

      // Form data
      newWebhook: {
        url: "",
        events: [],
      },

      // Secret result (shown only once)
      newSecretResult: null,

      // Selected webhook for viewing logs
      selectedWebhook: null,

      // Error/success messages
      error: null,
      success: null,
      copySuccess: false,

      // Confirmation modal
      showConfirmModal: false,
      confirmModalMessage: "",
      pendingDeleteId: null,

      // Test webhook
      testingWebhook: null,
      testResult: null,

      // Rotate webhook
      rotatingWebhook: null,
    };

    this.scrollRef = null;
    this.handleWheel = this.handleWheel.bind(this);
    this.setScrollRef = this.setScrollRef.bind(this);
  }

  setScrollRef(el) {
    if (this.scrollRef) {
      const oldContainer = this.scrollRef._container || this.scrollRef;
      if (oldContainer && oldContainer.removeEventListener) {
        oldContainer.removeEventListener("wheel", this.handleWheel);
      }
    }

    this.scrollRef = el;

    if (this.scrollRef) {
      const container = this.scrollRef._container || this.scrollRef;
      if (container && container.addEventListener) {
        container.addEventListener("wheel", this.handleWheel, {
          passive: false,
        });
      }
    }
  }

  handleWheel(e) {
    if (e.deltaY !== 0) {
      e.currentTarget.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }

  componentDidMount() {
    this.loadWebhooks();
  }

  getApiKey = () => {
    // Get API key from localStorage or partners
    const partners = JSON.parse(localStorage.getItem("api_partners") || "[]");
    if (
      partners.length > 0 &&
      partners[0].apiKeys &&
      partners[0].apiKeys.length > 0
    ) {
      return partners[0].apiKeys[0].key;
    }
    return null;
  };

  loadWebhooks = async () => {
    // Check permission: webhooks require pro_auto
    if (!this.checkPermission("pro_auto")) {
      this.setState({ loading: false, webhooks: [] });
      return;
    }
    this.setState({ loading: true, error: null });
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/api/partner-management/webhooks`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      this.setState({ webhooks: res.data.data || [], loading: false });
    } catch (error) {
      console.error("Load webhooks error:", error);
      this.setState({
        loading: false,
        webhooks: [],
        error: "",
      });
    }
  };

  checkPermission = (requiredPackage) => {
    const { license } = this.props;
    if (!license) return false;
    const ranks = {
      free: 0,
      basic: 1,
      pro: 2,
      pro_auto: 3,
      enterprise: 4,
    };
    const currentPackage = (license.purchased_package || "free").toLowerCase();
    const currentRank = ranks[currentPackage] || 0;
    const requiredRank = ranks[requiredPackage] || 0;

    if (currentRank > 0 && license.expired_date) {
      const today = new Date().toISOString().split("T")[0];
      if (license.expired_date < today) {
        return 0 >= requiredRank;
      }
    }
    return currentRank >= requiredRank;
  };

  handleCreateWebhook = async () => {
    const { newWebhook } = this.state;
    const { intl } = this.props;

    if (!newWebhook.url.trim()) {
      this.setState({
        error: intl.formatMessage({
          id: "settings.webhook.modal.create.url-label",
        }),
      });
      return;
    }

    if (newWebhook.events.length === 0) {
      this.setState({
        error: intl.formatMessage({
          id: "settings.webhook.modal.create.events-label",
        }),
      });
      return;
    }

    // Validate URL format
    try {
      new URL(newWebhook.url);
    } catch {
      this.setState({
        error: intl.formatMessage({ id: "settings.webhook.alert.create-fail" }),
      });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/api/partner-management/webhooks`,
        {
          url: newWebhook.url,
          events: newWebhook.events,
        },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      // Show the secret (only shown once!)
      this.setState({
        newSecretResult: res.data.data,
        showCreateModal: false,
        showSecretModal: true,
        loading: false,
        success: intl.formatMessage({
          id: "settings.webhook.alert.create-success",
        }),
        newWebhook: {
          url: "",
          events: [],
        },
      });

      this.loadWebhooks();
    } catch (error) {
      console.error("Create webhook error:", error);
      this.setState({
        loading: false,
        error:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          intl.formatMessage({ id: "settings.webhook.alert.create-fail" }),
      });
    }
  };

  handleDeleteWebhook = (id) => {
    this.setState({
      showConfirmModal: true,
      confirmModalMessage: this.props.intl.formatMessage({
        id: "settings.webhook.alert.delete-confirm",
      }),
      pendingDeleteId: id,
    });
  };

  executeDeleteWebhook = async () => {
    const { pendingDeleteId } = this.state;
    this.setState({ showConfirmModal: false, pendingDeleteId: null });

    try {
      const tokenStr = localStorage.getItem("access_token");
      await axios.delete(
        `${END_POINT}/api/partner-management/webhooks/${pendingDeleteId}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      this.setState({
        success: this.props.intl.formatMessage({
          id: "settings.webhook.alert.delete-success",
        }),
      });
      this.loadWebhooks();
    } catch (error) {
      console.error("Delete webhook error:", error);
      this.setState({
        error:
          this.props.intl.formatMessage({
            id: "settings.webhook.alert.delete-fail",
          }) +
          ": " +
          (error.response?.data?.message || error.message),
      });
    }
  };

  handleToggleWebhook = async (webhook) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      await axios.patch(
        `${END_POINT}/api/partner-management/webhooks/${webhook.id}`,
        { is_active: !webhook.is_active },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );
      this.loadWebhooks();
    } catch (error) {
      console.error("Toggle webhook error:", error);
      this.setState({
        error: this.props.intl.formatMessage({
          id: "settings.webhook.alert.update-fail",
        }),
      });
    }
  };

  handleTestWebhook = async (webhook) => {
    this.setState({ testingWebhook: webhook.id, testResult: null });
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/api/partner-management/webhooks/${webhook.id}/test`,
        {},
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );
      this.setState({
        testingWebhook: null,
        testResult: {
          webhook_id: webhook.id,
          success: res.data.success,
          message: res.data.message,
          response: res.data.response,
        },
      });
      setTimeout(() => this.setState({ testResult: null }), 5000);
    } catch (error) {
      this.setState({
        testingWebhook: null,
        testResult: {
          webhook_id: webhook.id,
          success: false,
          message: error.response?.data?.message || error.message,
        },
      });
      setTimeout(() => this.setState({ testResult: null }), 5000);
    }
  };

  handleRotateWebhook = async (webhook) => {
    if (
      !window.confirm(
        this.props.intl.formatMessage({
          id: "settings.webhook.alert.rotate-confirm",
        }),
      )
    ) {
      return;
    }

    this.setState({ rotatingWebhook: webhook.id });
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/api/partner-management/webhooks/${webhook.id}/rotate`,
        {},
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );
      this.setState({
        rotatingWebhook: null,
        newSecretResult: res.data.data,
        showSecretModal: true,
        success: this.props.intl.formatMessage({
          id: "settings.webhook.alert.rotate-success",
        }),
      });
      this.loadWebhooks();
    } catch (error) {
      console.error("Rotate webhook error:", error);
      this.setState({
        rotatingWebhook: null,
        error:
          error.response?.data?.message ||
          this.props.intl.formatMessage({
            id: "settings.webhook.alert.rotate-fail",
          }),
      });
    }
  };

  handleViewLogs = async (webhook) => {
    this.setState({ selectedWebhook: webhook, showLogsModal: true, logs: [] });
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/api/partner-management/webhooks/${webhook.id}/logs`,
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );
      this.setState({ logs: res.data.data || [] });
    } catch (error) {
      console.error("Load logs error:", error);
    }
  };

  copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    this.setState({ copySuccess: true });
    setTimeout(() => this.setState({ copySuccess: false }), 2000);
  };

  getWebhookEvents = () => {
    const { intl } = this.props;
    return [
      {
        value: "order.created",
        label: intl.formatMessage({
          id: "settings.webhook.event.order.created",
        }),
      },
      {
        value: "order.paid",
        label: intl.formatMessage({ id: "settings.webhook.event.order.paid" }),
      },
      {
        value: "order.shipped",
        label: intl.formatMessage({
          id: "settings.webhook.event.order.shipped",
        }),
      },
      {
        value: "order.completed",
        label: intl.formatMessage({
          id: "settings.webhook.event.order.completed",
        }),
      },
      {
        value: "order.cancelled",
        label: intl.formatMessage({
          id: "settings.webhook.event.order.cancelled",
        }),
      },
      {
        value: "product.updated",
        label: intl.formatMessage({
          id: "settings.webhook.event.product.updated",
        }),
      },
      {
        value: "inventory.low_stock",
        label: intl.formatMessage({
          id: "settings.webhook.event.inventory.low_stock",
        }),
      },
      {
        value: "inventory.expiring",
        label: intl.formatMessage({
          id: "settings.webhook.event.inventory.expiring",
        }),
      },
      {
        value: "customer.created",
        label: intl.formatMessage({
          id: "settings.webhook.event.customer.created",
        }),
      },
      {
        value: "payment.received",
        label: intl.formatMessage({
          id: "settings.webhook.event.payment.received",
        }),
      },
    ];
  };

  renderWebhooksList() {
    const { webhooks, loading, testingWebhook, testResult } = this.state;
    const WEBHOOK_EVENTS = this.getWebhookEvents();

    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="loading" />
        </div>
      );
    }

    if (webhooks.length === 0) {
      return (
        <Alert color="info">
          <IntlMessages id="settings.webhook.alert.empty" />
        </Alert>
      );
    }

    return (
      <PerfectScrollbar
        ref={this.setScrollRef}
        options={{ suppressScrollY: true, wheelPropagation: false }}
      >
        <Table responsive hover>
          <thead>
            <tr>
              <th>
                <IntlMessages id="settings.webhook.table.url" />
              </th>
              <th>
                <IntlMessages id="settings.webhook.table.events" />
              </th>
              <th>
                <IntlMessages id="settings.webhook.table.status" />
              </th>
              <th>
                <IntlMessages id="settings.webhook.table.last-triggered" />
              </th>
              <th>
                <IntlMessages id="settings.webhook.table.action" />
              </th>
            </tr>
          </thead>
          <tbody>
            {webhooks.map((webhook) => (
              <tr key={webhook.id}>
                <td className="align-middle" style={{ maxWidth: "250px" }}>
                  <code style={{ fontSize: "12px", wordBreak: "break-all" }}>
                    {webhook.url}
                  </code>
                </td>
                <td className="align-middle" style={{ maxWidth: "300px" }}>
                  <div className="d-flex flex-wrap">
                    {webhook.events &&
                      webhook.events.map((event) => {
                        const eventObj = WEBHOOK_EVENTS.find(
                          (e) => e.value === event,
                        );
                        return (
                          <Badge key={event} color="info" className="mr-1 mb-1">
                            {eventObj ? eventObj.label : event}
                          </Badge>
                        );
                      })}
                  </div>
                </td>
                <td className="align-middle">
                  <Badge color={webhook.is_active ? "success" : "danger"}>
                    {webhook.is_active ? (
                      <IntlMessages id="settings.webhook.status.active" />
                    ) : (
                      <IntlMessages id="settings.webhook.status.inactive" />
                    )}
                  </Badge>
                  {webhook.failure_count > 0 && (
                    <Badge color="warning" className="ml-1">
                      {webhook.failure_count} lỗi
                    </Badge>
                  )}
                </td>
                <td className="align-middle">
                  <small className="text-muted">
                    {webhook.last_triggered_at
                      ? new Date(webhook.last_triggered_at).toLocaleString()
                      : "-"}
                  </small>
                </td>
                <td className="align-middle" style={{ textAlign: "end" }}>
                  <div className="d-flex justify-content-end">
                    <Button
                      size="sm"
                      color={
                        webhook.is_active
                          ? "outline-secondary"
                          : "outline-success"
                      }
                      className="d-flex align-items-center justify-content-center px-3 py-0"
                      onClick={() => this.handleToggleWebhook(webhook)}
                      title={webhook.is_active ? "Tắt" : "Bật"}
                      style={{ marginRight: "4px" }}
                    >
                      <i
                        className={
                          webhook.is_active
                            ? "simple-icon-control-pause"
                            : "simple-icon-control-play"
                        }
                      />
                    </Button>
                    <Button
                      size="sm"
                      color="outline-info"
                      className="d-flex align-items-center justify-content-center px-3 py-0"
                      onClick={() => this.handleTestWebhook(webhook)}
                      disabled={testingWebhook === webhook.id}
                      title="Test"
                      style={{ marginRight: "4px" }}
                    >
                      {testingWebhook === webhook.id ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <i className="simple-icon-paper-plane" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      color="outline-primary"
                      className="d-flex align-items-center justify-content-center px-3 py-0"
                      onClick={() => this.handleViewLogs(webhook)}
                      title="Xem logs"
                      style={{ marginRight: "4px" }}
                    >
                      <i className="simple-icon-list" />
                    </Button>
                    <Button
                      size="sm"
                      color="outline-warning"
                      className="d-flex align-items-center justify-content-center px-3 py-0"
                      onClick={() => this.handleRotateWebhook(webhook)}
                      disabled={this.state.rotatingWebhook === webhook.id}
                      title="Tạo lại Secret Key"
                      style={{ marginRight: "4px" }}
                    >
                      {this.state.rotatingWebhook === webhook.id ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <i className="simple-icon-refresh" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      color="outline-danger"
                      className="d-flex align-items-center justify-content-center px-3 py-0"
                      onClick={() => this.handleDeleteWebhook(webhook.id)}
                      title="Xóa"
                    >
                      <i className="simple-icon-trash" />
                    </Button>
                  </div>
                  {testResult && testResult.webhook_id === webhook.id && (
                    <div className="mt-1 text-right">
                      <Badge color={testResult.success ? "success" : "danger"}>
                        {testResult.success ? "✓ OK" : "✗ Lỗi"}
                      </Badge>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </PerfectScrollbar>
    );
  }

  renderCreateModal() {
    const { showCreateModal, newWebhook, loading } = this.state;
    const WEBHOOK_EVENTS = this.getWebhookEvents();

    return (
      <Modal
        isOpen={showCreateModal}
        toggle={() => this.setState({ showCreateModal: false })}
        size="lg"
      >
        <ModalHeader toggle={() => this.setState({ showCreateModal: false })}>
          <IntlMessages id="settings.webhook.modal.create.title" />
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="mb-4">
            <strong>Webhook?</strong>{" "}
            <IntlMessages id="settings.webhook.modal.create.desc" />
          </Alert>

          <Form>
            <FormGroup>
              <Label>
                <IntlMessages id="settings.webhook.modal.create.url-label" />
              </Label>
              <Input
                type="url"
                value={newWebhook.url}
                onChange={(e) =>
                  this.setState({
                    newWebhook: { ...newWebhook, url: e.target.value },
                  })
                }
                placeholder={this.props.intl.formatMessage({
                  id: "settings.webhook.modal.create.url-placeholder",
                })}
              />
              <small className="text-muted">
                <IntlMessages id="settings.webhook.modal.create.url-help" />
              </small>
            </FormGroup>

            <FormGroup>
              <Label>
                <IntlMessages id="settings.webhook.modal.create.events-label" />
              </Label>
              {newWebhook.events.length > 0 && (
                <div className="mb-2">
                  {newWebhook.events.map((event) => {
                    const eventObj = WEBHOOK_EVENTS.find(
                      (e) => e.value === event,
                    );
                    return (
                      <Badge
                        key={event}
                        color="primary"
                        className="mr-1 mb-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const events = newWebhook.events.filter(
                            (e) => e !== event,
                          );
                          this.setState({
                            newWebhook: { ...newWebhook, events },
                          });
                        }}
                      >
                        {eventObj ? eventObj.label : event} ×
                      </Badge>
                    );
                  })}
                </div>
              )}
              <Select
                options={WEBHOOK_EVENTS.filter(
                  (e) => !newWebhook.events.includes(e.value),
                )}
                placeholder={this.props.intl.formatMessage({
                  id: "settings.webhook.modal.create.events-placeholder",
                })}
                onChange={(selected) => {
                  if (selected) {
                    this.setState({
                      newWebhook: {
                        ...newWebhook,
                        events: [...newWebhook.events, selected.value],
                      },
                    });
                  }
                }}
                value={null}
                isClearable={false}
              />
              <small className="text-muted">
                <IntlMessages id="settings.webhook.modal.create.events-help" />
              </small>
            </FormGroup>

            <Card className="bg-light">
              <CardBody className="p-3">
                <h6 className="mb-2">📚 Payload mẫu:</h6>
                <pre
                  style={{
                    fontSize: "12px",
                    margin: 0,
                    background: "#2d3748",
                    color: "#e2e8f0",
                    padding: "10px",
                    borderRadius: "5px",
                    overflow: "auto",
                  }}
                >
                  {`{
  "id": "evt_123456789",
  "type": "order.created",
  "created_at": "2024-01-07T10:00:00Z",
  "data": {
    "order_id": "abc-123",
    "total_amount": 150000,
    ...
  }
}`}
                </pre>
              </CardBody>
            </Card>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => this.setState({ showCreateModal: false })}
          >
            <IntlMessages id="settings.webhook.modal.create.cancel" />
          </Button>
          <Button
            color="primary"
            onClick={this.handleCreateWebhook}
            disabled={loading}
          >
            {loading ? (
              <IntlMessages id="settings.webhook.modal.create.submitting" />
            ) : (
              <IntlMessages id="settings.webhook.modal.create.submit" />
            )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderSecretModal() {
    const { showSecretModal, newSecretResult, copySuccess } = this.state;

    if (!newSecretResult) return null;

    return (
      <Modal
        isOpen={showSecretModal}
        toggle={() =>
          this.setState({ showSecretModal: false, newSecretResult: null })
        }
      >
        <ModalHeader
          toggle={() =>
            this.setState({ showSecretModal: false, newSecretResult: null })
          }
        >
          <IntlMessages id="settings.webhook.modal.secret.title" />
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong>
              <IntlMessages id="settings.webhook.modal.secret.important" />
            </strong>
          </Alert>

          <FormGroup>
            <Label>
              <IntlMessages id="settings.webhook.modal.secret.label" />
            </Label>
            <div className="d-flex">
              <Input
                type="text"
                value={newSecretResult.secret || ""}
                readOnly
                className="font-monospace"
                style={{ fontSize: "12px" }}
              />
              <Button
                color="primary"
                className="ml-2"
                onClick={() => this.copyToClipboard(newSecretResult.secret)}
              >
                {copySuccess ? (
                  <IntlMessages id="settings.webhook.modal.secret.copied" />
                ) : (
                  <IntlMessages id="settings.webhook.modal.secret.copy" />
                )}
              </Button>
            </div>
          </FormGroup>

          <Card className="bg-light">
            <CardBody className="p-3">
              <h6 className="mb-2">
                <IntlMessages id="settings.webhook.modal.secret.verify-guide" />
              </h6>
              <pre
                style={{
                  fontSize: "11px",
                  margin: 0,
                  background: "#2d3748",
                  color: "#e2e8f0",
                  padding: "10px",
                  borderRadius: "5px",
                  overflow: "auto",
                }}
              >
                {`// Node.js example
const crypto = require('crypto');

const signature = req.headers['x-vmass-signature'];
const payload = JSON.stringify(req.body);
const expected = crypto
  .createHmac('sha256', 'YOUR_SECRET')
  .update(payload)
  .digest('hex');

if (signature !== expected) {
  return res.status(401).send('Invalid');
}`}
              </pre>
            </CardBody>
          </Card>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() =>
              this.setState({ showSecretModal: false, newSecretResult: null })
            }
          >
            <IntlMessages id="settings.webhook.modal.secret.close" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderLogsModal() {
    const { showLogsModal, selectedWebhook, logs } = this.state;

    return (
      <Modal
        isOpen={showLogsModal}
        toggle={() => this.setState({ showLogsModal: false })}
        size="lg"
      >
        <ModalHeader toggle={() => this.setState({ showLogsModal: false })}>
          <IntlMessages id="settings.webhook.modal.logs.title" />
          {selectedWebhook && (
            <small className="d-block text-muted" style={{ fontSize: "12px" }}>
              {selectedWebhook.url}
            </small>
          )}
        </ModalHeader>
        <ModalBody>
          {logs.length === 0 ? (
            <Alert color="info">
              <IntlMessages id="settings.webhook.modal.logs.empty" />
            </Alert>
          ) : (
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th>
                    <IntlMessages id="settings.webhook.modal.logs.time" />
                  </th>
                  <th>
                    <IntlMessages id="settings.webhook.modal.logs.event" />
                  </th>
                  <th>
                    <IntlMessages id="settings.webhook.modal.logs.status" />
                  </th>
                  <th>
                    <IntlMessages id="settings.webhook.modal.logs.response-time" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <small>{new Date(log.createdAt).toLocaleString()}</small>
                    </td>
                    <td>
                      <Badge color="info">{log.event_type}</Badge>
                    </td>
                    <td>
                      <Badge color={log.success ? "success" : "danger"}>
                        {log.status_code || "Error"}
                      </Badge>
                    </td>
                    <td>{log.response_time_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => this.setState({ showLogsModal: false })}
          >
            <IntlMessages id="settings.webhook.modal.logs.close" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderConfirmModal() {
    const { showConfirmModal, confirmModalMessage } = this.state;

    return (
      <Modal
        isOpen={showConfirmModal}
        toggle={() => this.setState({ showConfirmModal: false })}
      >
        <ModalHeader toggle={() => this.setState({ showConfirmModal: false })}>
          <IntlMessages id="settings.webhook.modal.confirm.title" />
        </ModalHeader>
        <ModalBody>
          <p>{confirmModalMessage}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() =>
              this.setState({ showConfirmModal: false, pendingDeleteId: null })
            }
          >
            <IntlMessages id="settings.webhook.modal.confirm.cancel" />
          </Button>
          <Button color="danger" onClick={this.executeDeleteWebhook}>
            <IntlMessages id="settings.webhook.modal.confirm.confirm" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  render() {
    const { error, success } = this.state;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>
            <IntlMessages id="settings.webhook.title" />
          </h4>
          <div>
            <Button
              color="outline-info"
              className="mr-2"
              href={`${END_POINT}/api/public/docs`}
              target="_blank"
              style={{ marginRight: "10px" }}
            >
              <i className="simple-icon-book-open mr-2" />
              <IntlMessages id="settings.webhook.doc-btn" />
            </Button>
            {!this.checkPermission("pro_auto") ? (
              <Button
                color="secondary"
                disabled
                title={this.props.intl.formatMessage({
                  id: "settings.webhook.pro-feature-tooltip",
                })}
              >
                <i className="simple-icon-lock mr-2" />
                <IntlMessages id="settings.webhook.create-btn-pro-auto" />
              </Button>
            ) : (
              <Button
                color="primary"
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <IntlMessages id="settings.webhook.create-btn" />
              </Button>
            )}
          </div>
        </div>

        {!this.checkPermission("pro_auto") && (
          <Alert color="warning" className="mb-4">
            <IntlMessages id="settings.webhook.upgrade-alert.text" />{" "}
            <strong>Pro Auto</strong>{" "}
            <IntlMessages id="settings.webhook.upgrade-alert.action" />{" "}
            <a
              href="/app/applications/upgrade"
              className="alert-link font-weight-bold"
            >
              <IntlMessages id="settings.webhook.upgrade-alert.link" />
            </a>
          </Alert>
        )}

        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            color="success"
            toggle={() => this.setState({ success: null })}
          >
            {success}
          </Alert>
        )}

        <Card>
          <CardBody>{this.renderWebhooksList()}</CardBody>
        </Card>

        {this.renderCreateModal()}
        {this.renderSecretModal()}
        {this.renderLogsModal()}
        {this.renderConfirmModal()}
      </div>
    );
  }
}

export default injectIntl(WebhookManagement);
